/**
 * 开发模式工具函数
 * 用于在开发环境下模拟登录和认证状态
 */

import { DEV_MODE } from '../config.js'

function getDevUserId() {
  try {
    // 优先从 MOCK_USER.id 读取，如果没有则从 USER_ID 读取，最后默认 '1'
    return DEV_MODE?.MOCK_USER?.id || DEV_MODE?.USER_ID || '1'
  } catch (_) {
    return '1'
  }
}

/**
 * 检查开发模式是否启用
 * @returns {boolean} 开发模式是否启用
 */
export function isDevModeEnabled() {
  try {
    return DEV_MODE && DEV_MODE.ENABLED === true
  } catch (e) {
    console.error('检查开发模式状态失败:', e)
    return false
  }
}

/**
 * 检查开发模式下是否已登录
 * @returns {boolean} 是否已登录
 */
export function isDevModeLoggedIn() {
  if (!isDevModeEnabled()) {
    return false
  }
  
  try {
    // 检查是否有开发模式的登录标记
    const devModeLoggedIn = localStorage.getItem('dev_mode_logged_in')
    return devModeLoggedIn === 'true'
  } catch (e) {
    console.error('检查开发模式登录状态失败:', e)
    return false
  }
}

/**
 * 开发模式下的认证检查
 * 返回模拟的认证状态
 * @returns {Promise<Object>} 模拟的认证状态信息
 */
export async function devModeCheckCertification() {
  if (!isDevModeEnabled()) {
    throw new Error('开发模式未启用')
  }
  
  // 返回模拟的认证通过状态
  return {
    certificationState: 1, // 1: 认证通过
    userInfo: {
      id: 'dev_mode_user',
      name: '开发模式用户',
      status: 1
    }
  }
}

/**
 * 开发模式登录
 * 调用后端mockLogin接口获取真实的用户信息和token
 * @param {string} userId - 可选，指定要模拟登录的用户ID，默认从 DEV_MODE.USER_ID 读取
 * @returns {Promise<Object>} 登录结果，包含用户信息
 */
export async function devModeLogin(userId = getDevUserId()) {
  if (!isDevModeEnabled()) {
    throw new Error('开发模式未启用')
  }
  
  try {
    console.info('[devModeLogin] start mockLogin with userId:', userId)
    // 动态导入axios，避免循环依赖
    const { defHttp } = await import('./axios.js')
    
    // 调用后端mockLogin接口，按后端要求传入userId
    const response = await defHttp.post('/app/auth/mockLogin', null, {
      params: { userId }
    })
    const data = response?.data || {}
    // 兼容后端可能返回 { code, data: { token, user } } 的结构
    const payload = data?.data || data
    console.info('[devModeLogin] mockLogin response payload:', payload)
    
    // 检查登录是否成功
    const token = payload.token
    const user = payload.user || payload.userInfo
    console.log('[devModeLogin] 解析用户信息:', {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      hasUser: !!user,
      userKeys: user ? Object.keys(user) : [],
      userId: user?.id || user?.uid || payload.userId,
      payloadKeys: Object.keys(payload)
    })
    
    if (token) {
      // 保存token和用户ID
      localStorage.setItem('auth_token', token)
      const parsedUserId = user?.id || user?.uid || payload.userId || payload.uid
      console.log('[devModeLogin] 准备保存用户ID:', parsedUserId)
      
      if (parsedUserId) {
        localStorage.setItem('user_id', String(parsedUserId))
        console.log('[devModeLogin] ✅ 已保存用户ID:', parsedUserId)
      } else {
        console.error('[devModeLogin] ⚠️ 未找到用户ID，payload:', payload)
        // 尝试从token中解析uid（JWT格式）
        try {
          const tokenParts = token.split('.')
          if (tokenParts.length === 3) {
            const payloadPart = JSON.parse(atob(tokenParts[1]))
            if (payloadPart.uid) {
              localStorage.setItem('user_id', String(payloadPart.uid))
              console.log('[devModeLogin] ✅ 从token中解析并保存用户ID:', payloadPart.uid)
            }
          }
        } catch (e) {
          console.error('[devModeLogin] ❌ 从token解析用户ID失败:', e)
        }
      }
      
      // 设置开发模式登录标记
      localStorage.setItem('dev_mode_logged_in', 'true')
      
      console.log('[devModeLogin] ✅ 登录成功，已保存:', {
        hasToken: !!localStorage.getItem('auth_token'),
        userId: localStorage.getItem('user_id'),
        devModeLoggedIn: localStorage.getItem('dev_mode_logged_in')
      })
      
      // 返回用户信息（格式与微信登录保持一致）
      return {
        user: user || {
          id: payload.userId || 'dev_mode_user',
          uid: payload.userId || 'dev_mode_user',
          name: payload.userName || '开发模式用户',
          certificationState: payload.certificationState || 0,
          status: payload.status || 1
        },
        token,
        loginComplete: true
      }
    } else {
      throw new Error(data.message || '登录失败：未获取到token')
    }
  } catch (e) {
    console.error('开发模式登录失败:', e)
    
    // 如果是网络错误或接口错误，抛出详细错误信息
    if (e.response) {
      const errorMsg = e.response?.data?.message || e.response?.data?.msg || '登录失败，请稍后重试'
      throw new Error(errorMsg)
    }
    
    throw new Error('开发模式登录失败: ' + (e.message || '未知错误'))
  }
}

export default {
  isDevModeEnabled,
  isDevModeLoggedIn,
  devModeCheckCertification,
  devModeLogin
}


